import axios from "axios";
import { ENDPOINT } from "constants/environment";
import { encryptData } from "helpers";
import { decryptData } from "helpers";
import { getHeaderConfig } from "helpers";
import { consoleLog } from "helpers";

export const getCountriesAPI = async (
  { pageIndex, skip },
  isLoading,
  accessJWT
) => {
  try {
    isLoading(true);
    const config = getHeaderConfig(null, accessJWT);

    const body = {
      skip: pageIndex + 1,
      limit: skip,
    };

    const response = await axios.post(
      `${ENDPOINT}/app/v1/county/search`,
      encryptData(body),
      config
    );
    const parsedResponseData = JSON.parse(decryptData(response?.data));
    if (parsedResponseData && parsedResponseData?.statusCode === 200) {
      if (isLoading) isLoading(false);
      consoleLog([parsedResponseData, "response"]);
      return parsedResponseData?.data;
    }
    if (isLoading) isLoading(false);
    isLoading(false);
    throw Error;
  } catch (error) {
    isLoading(false);
    console.error("Error fetching data:", error);
    return null;
  }
};

/**
 * The full country list — the dial-code / ISO source for every country picker
 * on the signed-in CRM screens (held once per login by
 * admin/contexts/CountriesContext.jsx).
 *
 * A legacy GET route, so it is called here directly rather than through
 * portalClient, which is POST-only. That also means no refresh-on-401: the
 * provider fires it straight after login, while the token is fresh. It needs
 * an `x-access-token` (401 without one), which is why the pre-login partner
 * signup form still reads the bundled Phone_Codes.json instead.
 *
 * Returns the raw `data` rows, or `null` on any failure — logged with the
 * HTTP status and the server's message; the provider then falls back to the
 * bundled Phone_Codes.json.
 */
const parseCountryBody = (raw) => {
  try {
    // app-backend encrypts an already-serialised payload, so the decrypted
    // value is usually a JSON string that needs a second parse.
    const once = decryptData(raw);
    return typeof once === "string" ? JSON.parse(once) : once;
  } catch {
    // Not ciphertext (server-side crypto off): plain JSON, or already parsed.
    try {
      return typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch {
      return null;
    }
  }
};

export const fetchCountryList = async (accessJWT) => {
  try {
    const response = await axios.get(
      `${ENDPOINT}/app/v1/county`,
      getHeaderConfig(null, accessJWT)
    );
    const body = parseCountryBody(response?.data);
    if (body?.statusCode === 200 && Array.isArray(body?.data)) {
      return body.data;
    }
    console.error("[countries] GET /app/v1/county failed:", body?.statusCode, body?.message || "");
    return null;
  } catch (error) {
    const body = error?.response ? parseCountryBody(error.response.data) : null;
    console.error(
      "[countries] GET /app/v1/county failed:",
      error?.response?.status ?? error?.message,
      body?.message || body?.error?.message || ""
    );
    return null;
  }
};

export const getFilterCountriesAPI = async (
  { searchQuery, pageIndex, limit },
  payload,
  isLoading,
  accessJWT
) => {
  try {
    isLoading(true);
    const config = getHeaderConfig(null, accessJWT);

    const body = {
      searchQuery,
      skip: pageIndex + 1,
      limit: limit || 10,
    };

    const response = await axios.post(
      `${ENDPOINT}/app/v1/county/search`,
      encryptData(body),
      config
    );

    const parsedResponseData = JSON.parse(decryptData(response?.data));

    if (parsedResponseData?.statusCode === 200) {
      if (isLoading) isLoading(false);
      consoleLog([parsedResponseData.data, "searchRes"]);
      consoleLog([
        parsedResponseData.data.pagination?.totalPages,
        "parsedResponseData.data.pagination?.totalPages",
      ]);

      return {
        data: parsedResponseData?.data.data,
        totalPages: parsedResponseData.data.pagination?.totalPages,
      };
    }

    if (isLoading) isLoading(false);
    throw new Error("Error fetching data");
  } catch (error) {
    isLoading(false);
    console.error("Error fetching data:", error);
    return { data: [], totalPages: 0 };
  }
};

export const createOrUpdateCountriesAPI = async (
  args,
  isLoading,
  accessJWT
) => {
  const API_URL = `${ENDPOINT}/app/v1/county`;
  try {
    if (isLoading) isLoading(true);
    const config = getHeaderConfig(null, accessJWT);
    const response = args?.id
      ? await axios.put(API_URL, encryptData(args), config)
      : await axios.post(API_URL, encryptData(args), config);
    const parsedResponseData = JSON.parse(decryptData(response?.data));
    if (parsedResponseData && parsedResponseData?.statusCode === 200) {
      if (isLoading) isLoading(false);
      return { success: true, data: parsedResponseData?.data };
    } else {
      if (isLoading) isLoading(false);
      return {
        success: false,
        message: parsedResponseData?.message || "Unknown error occurred",
      };
    }
  } catch (error) {
    if (isLoading) isLoading(false);
    console.error("Error creating/updating country:", error);

    if (error.response && error.response.data) {
      const backendMessage = error.response.data.message || "Unknown error";
      return { success: false, message: backendMessage };
    }

    return { success: false, message: "Network or server error" };
  }
};

export const deleteCountryAPI = async (args, isLoading, accessJWT) => {
  const API_URL = `${ENDPOINT}/app/v1/county?id=${args.id}`;
  consoleLog([args, "args"]);
  try {
    if (isLoading) isLoading(true);
    const config = getHeaderConfig(null, accessJWT);
    const response = await axios.delete(API_URL, config);
    const parsedResponseData = JSON.parse(decryptData(response?.data));
    if (parsedResponseData) {
      if (isLoading) isLoading(false);
      return parsedResponseData;
    }
    if (isLoading) isLoading(false);
    isLoading(false);
    throw Error;
  } catch (error) {
    isLoading(false);
    console.error("Error deleting country:", error);
    return null;
  }
};
